import { isVersionGreaterOrEqual } from "./version"

interface Context {
  app_version: string
  immersive: boolean
  appearance: "light" | "dark"
  currency: string
  locale: string
  platform: "iOS" | "Android" | "Desktop"
  conversation_id: string
  [props: string]: unknown
}
interface Messager {
  getContext: () => Context
  playlist: (audios: string[]) => unknown
  reloadTheme: (args?: string) => void
  showToast: (msg: string) => void
}
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        MixinContext?: unknown
        playlist?: { postMessage: Messager["playlist"] }
        reloadTheme?: { postMessage: Messager["reloadTheme"] }
      }
    }
    MixinContext?: {
      getContext?: Messager["getContext"]
      playlist?: Messager["playlist"]
      reloadTheme?: Messager["reloadTheme"]
      showToast?: Messager["showToast"]
    }
  }
}

export function messager<T extends keyof Messager>(type: T) {
  const isIOS = !!window?.webkit?.messageHandlers?.MixinContext
  const isAndroid = !!window?.MixinContext
  if (!isIOS && !isAndroid) return

  const handler = isIOS
    ? type === "getContext"
      ? () => JSON.parse(prompt("MixinContext.getContext()") as string) as Context
      : (window?.webkit?.messageHandlers?.[type as Exclude<T, "getContext" | "showToast">]?.postMessage.bind(
          window?.webkit?.messageHandlers?.[type as Exclude<T, "getContext" | "showToast">]
        ) as Messager[Exclude<T, "getContext">])
    : (window?.MixinContext?.[type]?.bind(window?.MixinContext) as Messager[T])

  if (type === "getContext") {
    return () => {
      let context = (handler as Messager["getContext"])()
      if (typeof context === "string") {
        context = JSON.parse(context)
      }

      return context as Context
    }
  }

  return handler
}

export const isValidMixinVersion = () => {
  if (import.meta.env.MODE !== "production") return true

  const currentVersion = messager("getContext")?.()?.app_version
  if (!currentVersion) return
  return isVersionGreaterOrEqual(currentVersion, "1.0.0")
}
